import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { aboutTranslations } from '../data/translations/aboutTranslations';
import { aboutAreasData } from '../data/aboutAreasData';
import { usePortfolioFilter } from '../context/PortfolioFilterContext';

// Icons per specialty — purely decorative, Material Symbols names
const areaIcons = {
  imagenologia: 'radiology',
  dermatologia: 'dermatology',
  saludMental: 'neurology',
  cardiologia: 'cardiology',
  laboratorios: 'biotech',
};

const AreaTile = ({ id, category, index, t, onClick }) => {
  const label = t.areas[id];
  const icon = areaIcons[id];
  const num = String(index + 1).padStart(2, '0');

  return (
    <motion.button
      onClick={onClick}
      aria-label={`${t.viewPortfolio}: ${label}`}
      className="group relative w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
      whileHover="hovered"
      initial="idle"
    >
      {/* Top rule */}
      <motion.div
        className="h-px w-full bg-border mb-5 origin-left"
        variants={{ idle: { scaleX: 1, backgroundColor: 'var(--color-border)' }, hovered: { backgroundColor: 'var(--color-secondary)' } }}
        transition={{ duration: 0.3 }}
      />

      <div className="flex items-center justify-between gap-6 pr-2">
        {/* Left: index + label */}
        <div className="flex items-center gap-5">
          {/* Index number */}
          <span className="font-headline text-[10px] font-black text-primary/20 tracking-[0.3em] group-hover:text-secondary transition-colors duration-300 leading-none tabular-nums select-none">
            {num}
          </span>

          {/* Label */}
          <span className="font-headline text-xl md:text-2xl font-extrabold text-primary uppercase tracking-tight group-hover:text-secondary transition-colors duration-300 leading-none">
            {label}
          </span>
        </div>

        {/* Right: icon + arrow */}
        <div className="flex items-center gap-3 shrink-0">
          <span className="material-symbols-outlined text-3xl text-primary/20 group-hover:text-secondary/60 transition-colors duration-300">
            {icon}
          </span>
          <motion.span
            className="material-symbols-outlined text-base text-secondary"
            variants={{ idle: { x: 0, opacity: 0 }, hovered: { x: 4, opacity: 1 } }}
            transition={{ duration: 0.2 }}
          >
            arrow_forward
          </motion.span>
        </div>
      </div>

      {/* Hover label */}
      <motion.span
        className="block mt-3 text-[9px] font-bold uppercase tracking-[0.25em] text-secondary"
        variants={{ idle: { opacity: 0, y: -4 }, hovered: { opacity: 1, y: 0 } }}
        transition={{ duration: 0.2 }}
      >
        {t.viewPortfolio}
      </motion.span>
    </motion.button>
  );
};

export default function About() {
  const { language } = useLanguage();
  const t = aboutTranslations[language].about;
  const { setActiveCategory } = usePortfolioFilter();

  const handleAreaClick = (category) => {
    setActiveCategory(category);
    const section = document.getElementById('servicios');
    if (section) section.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-background text-on-surface font-body overflow-hidden">
      <section className="py-24 px-8 bg-background" id="about">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">

            {/* Left Column: Text Content */}
            <div className="lg:sticky lg:top-32">
              <h2 className="font-headline text-4xl font-extrabold text-primary leading-tight mb-8">
                {t.title}
              </h2>
              <p className="opacity-60 text-lg leading-relaxed mb-10">
                {t.description}
              </p>
              <div className="space-y-6">
                <div>
                  <strong className="text-primary font-bold">{t.features.neutrality.title}</strong>
                  <p className="opacity-60 mt-1 text-base">{t.features.neutrality.text}</p>
                </div>
                <div>
                  <strong className="text-primary font-bold">{t.features.independence.title}</strong>
                  <p className="opacity-60 mt-1 text-base">{t.features.independence.text}</p>
                </div>
              </div>
            </div>

            {/* Right Column: Areas as Index */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              {/* Section label — no bounding box */}
              <p className="text-[10px] font-black text-primary/30 uppercase tracking-[0.4em] mb-10">
                {t.areasTitle}
              </p>

              {/* Staggered area tiles */}
              <div className="flex flex-col gap-8">
                {aboutAreasData.map((area, index) => (
                  <motion.div
                    key={area.id}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                  >
                    <AreaTile
                      id={area.id}
                      category={area.category}
                      index={index}
                      t={t}
                      onClick={() => handleAreaClick(area.category)}
                    />
                  </motion.div>
                ))}

                {/* Bottom rule */}
                <div className="h-px w-full bg-border mt-2" />
              </div>
            </motion.div>

          </div>
        </div>
      </section>
    </div>
  );
}